import { FC, PropsWithChildren, createContext, useContext, useEffect, useMemo } from 'react';
import { StoreApi, createStore, useStore } from 'zustand';
import { strings } from '../translation/strings';

export interface ConfirmAction {
  label: string;
  callback: () => void;
}

export interface ConfirmationStore {
  registerForConfirmation: (message: string, label?: string) => void;
  unregisterForConfirmation: () => void;
  onProceedCallback?: () => void;
  registerConfirmAction: (action: ConfirmAction) => void;
  action?: ConfirmAction;
  confirmationMessage?: string;
  confirmButtonLabel?: string;
  confirmationRequested: boolean;
  confirmationNeeded: boolean;
  requestConfirmation: (onProceed: () => void) => void;
  clearRequest: () => void;
}

export const ConfirmationContext = createContext<StoreApi<ConfirmationStore> | null>(null);

export const ConfirmationProvider: FC<PropsWithChildren> = ({ children }) => {
  const store = useMemo(() => {
    return createStore<ConfirmationStore>()((set, get) => ({
      registerForConfirmation: (message: string, label?: string) => {
        set({
          confirmationMessage: message,
          confirmationNeeded: true,
          confirmButtonLabel: label || strings.common.confirm
        });
      },
      unregisterForConfirmation: () => {
        set({
          onProceedCallback: undefined,
          confirmationMessage: undefined,
          confirmationRequested: false,
          confirmationNeeded: false,
          confirmButtonLabel: undefined,
          action: undefined
        });
      },
      registerConfirmAction: (action: ConfirmAction) => {
        set({
          action
        });
      },
      requestConfirmation: (callback: () => void) => {
        if (!get().confirmationNeeded) {
          // If there's nobody registered with the service, just proceed.
          callback();
          return;
        }

        set({
          onProceedCallback: () => {
            callback();
            get().unregisterForConfirmation();
          },
          confirmationRequested: true
        });
      },
      clearRequest: () => {
        set({ confirmationRequested: false });
      },
      confirmationNeeded: false,
      confirmationRequested: false
    }));
  }, []);

  return <ConfirmationContext.Provider value={store}>{children}</ConfirmationContext.Provider>;
};

type SelectorReturn<S extends (s: ConfirmationStore) => any> = ReturnType<S>;
export function useConfirm<S extends (s: ConfirmationStore) => any>(selector: S): SelectorReturn<S> {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmationProvider');
  }
  return useStore(context, selector);
}

export const useUnsavedChangesWarning = (isDirty: boolean) => {
  const { registerForConfirmation, unregisterForConfirmation } = useConfirm(state => ({
    registerForConfirmation: state.registerForConfirmation,
    unregisterForConfirmation: state.unregisterForConfirmation
  }));

  useEffect(() => {
    if (isDirty) registerForConfirmation(strings.settings.unsaved);
    return () => unregisterForConfirmation();
  }, [isDirty, registerForConfirmation, unregisterForConfirmation]);
};
